import React, { useState, useEffect } from 'react';
import { Row, Col, Button } from 'react-bootstrap';
import { useParams } from 'react-router-dom';
import * as Yup from 'yup';
import { Formik } from 'formik';
import * as Constants from '../../../../../helper/constants';
import { bgvApi } from '../../bgv-api/BgvApi';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import dynamicUrl from '../../../../../helper/dynamicUrls';
import { bgvAlerts } from '../../bgv-api/bgvAlerts';
import { isEmptyObject, areFilesInvalid } from '../../../../../util/utils';
import {
  evidenceUploadStatusOptions,
  evidenceUploadStatusOptionsArray,
  evidenceFileAvailabilityOptionsArray
} from '../../../../../helper/selectOptions';
import useFullPageLoader from '../../../../../helper/useFullPageLoader';
import DateInput from '../../../../../components/Shared/DateInput';
import MultiFileUpload from '../../../../../components/MultiFileUpload/MultiFileUpload';
import EvidenceGalleryModal from '../../../../../components/EvidenceGalleryModal/EvidenceGalleryModal';
import * as commonApi from '../../../../../api/commonApi';
import ChangeStatus from './Components/ChangeStatus';
import { dbMapping } from '../../bgv-api/dbMapping';

const OfacBgv = ({ className, rest, ofacData, reloadData, loadStatus, setLoadStatus }) => {
  let { case_id } = useParams();
  const [loader, showLoader, hideLoader] = useFullPageLoader();
  const [variableComponentStatus, setVariableComponentStatus] = useState('Awaiting');
  const [isDisabled, setIsDisabled] = useState(true);
  const [submitButtonActive, setSubmitButtonActive] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [showGallery, setShowGallery] = useState(false);

  useEffect(() => {
    if (sessionStorage.getItem('user_category') === Constants.PortalUserRoles.OpsSupervisor || sessionStorage.getItem('user_category') === Constants.PortalUserRoles.OpsTeam) {
      setIsDisabled(false);
      setSubmitButtonActive(true);
    }
  }, []);

  const sweetAlertHandler = (alert) => {
    const MySwal = withReactContent(Swal);
    MySwal.fire({
      title: alert.title,
      text: alert.text,
      icon: alert.type
    });
  };

  const _sendofacVerification = async (data) => {
    let apiUrl = dynamicUrl.uploadEvidenceUrl;
    const bgvResponse = await bgvApi(apiUrl, data, 'OFAC', case_id);

    if (bgvResponse.Error) {
      sweetAlertHandler(bgvAlerts.verificationFailed);
      hideLoader();
    } else {
      let uploadParams = bgvResponse.data;
      if (Array.isArray(uploadParams)) {
        for (let index = 0; index < uploadParams.length; index++) {
          let keyNameArr = Object.keys(uploadParams[index]);
          let keyName = keyNameArr[0];

          if (keyName === 'ofacEvidenceArray') {
            const urlsArray = uploadParams[index][keyName];
            for (let fileIdx = 0; fileIdx < selectedFiles.length; fileIdx++) {
              if (urlsArray[fileIdx]) {
                await fetch(urlsArray[fileIdx], {
                  method: 'PUT',
                  body: selectedFiles[fileIdx]
                });
              }
            }
          } else {
            let blobField = document.getElementById(keyName).files[0];
            let tempObj = uploadParams[index];
            await fetch(tempObj[keyName], {
              method: 'PUT',
              body: blobField
            });
          }
        }
      }
      sweetAlertHandler(bgvAlerts.verificationSuccess);
      reloadData();
      hideLoader();
      setLoadStatus(!loadStatus);
      setSelectedFiles([]);
    }
  };

  const saveComponentStatus = (e) => {
    setVariableComponentStatus(e.target.value);
  };

  const handleFilesChange = (files) => {
    setSelectedFiles(files);
  };

  const handleDeleteExistingFile = async (index) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: 'Do you want to delete this file?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, delete it!'
    });

    if (result.isConfirmed) {
      showLoader();
      try {
        const response = await commonApi.deleteEvidenceFile({
          data: {
            component_name: "OFAC",
            fileIndex: index,
            case_id: case_id
          }
        });

        if (response.error) {
          Swal.fire('Error', 'Failed to delete file', 'error');
        } else {
          Swal.fire('Deleted!', 'File has been deleted.', 'success');
          reloadData();
        }
      } catch (error) {
        console.error('Delete error:', error);
        Swal.fire('Error', 'Failed to delete file', 'error');
      } finally {
        hideLoader();
      }
    }
  };

  const handleViewGallery = () => {
    setShowGallery(true);
  };

  const handleGalleryDelete = async (index) => {
    setShowGallery(false);
    await handleDeleteExistingFile(index);
  };

  return (
    <>
      {ofacData !== 'N.A.' && (
        <>
          <Formik
            initialValues={{
              componentStatus: ofacData?.componentStatus || '',
              statusDescription: ofacData?.statusDescription || '',
              completedDate: ofacData?.completedDate || ''
            }}
            validationSchema={Yup.object().shape({
              componentStatus: Yup.string()
                .min(0, Constants.VerificationCommon.InvalidDefault)
                .oneOf(evidenceUploadStatusOptionsArray, Constants.VerificationCommon.InvalidDefault)
                .required(Constants.VerificationCommon.ChangeStatusRequired),
              statusDescription: evidenceFileAvailabilityOptionsArray.includes(variableComponentStatus)
                ? Yup.string()
                  .trim()
                  .min(2, Constants.VerificationCommon.StatusDescriptionTooShort)
                  .max(Constants.formikValueConstant.remarkCharacterLimit, Constants.VerificationCommon.StatusDescriptionTooLong)
                  .required(Constants.VerificationCommon.StatusDescriptionRequired)
                : Yup.string()
                  .trim()
                  .min(2, Constants.VerificationCommon.StatusDescriptionTooShort)
                  .max(Constants.formikValueConstant.remarkCharacterLimit, Constants.VerificationCommon.StatusDescriptionTooLong)
            })}
            onSubmit={async (values) => {
              showLoader();

              // Support both array format (new) and single file format (legacy)
              let existingFiles = ofacData?.ofacEvidence?.ofacEvidenceArray || [];
              // Fallback to legacy single file if array is empty
              if (existingFiles.length === 0 && ofacData?.ofacEvidence?.ofacEvidenceFile) {
                existingFiles = [ofacData.ofacEvidence.ofacEvidenceFile];
              }
              const hasFiles = selectedFiles.length > 0 || existingFiles.length > 0;

              if (!hasFiles && evidenceFileAvailabilityOptionsArray.includes(values.componentStatus)) {
                sweetAlertHandler(bgvAlerts.noFilesPresent);
                hideLoader();
                return;
              }

              if (selectedFiles.length > 0 && areFilesInvalid(selectedFiles) !== 0) {
                sweetAlertHandler(bgvAlerts.invalidFilesPresent);
                hideLoader();
                return;
              }

              const fileNames = selectedFiles.map(file => file.name);

              let combinedValues = {
                ofacEvidence: {
                  ofacEvidenceArray: fileNames,
                  ofacEvidenceFiles: []
                },
                componentStatus: values.componentStatus,
                statusDescription: values.statusDescription,
                completedDate: values.completedDate
              };

              _sendofacVerification(combinedValues);
            }}
          >
            {({ errors, handleBlur, handleChange, handleSubmit, touched, values, setFieldValue }) => (
              <form noValidate onSubmit={handleSubmit} className={className} {...rest}>
                <fieldset disabled={isDisabled}>
                  <Row>
                    <Col sm={12}>
                      <br />
                      <h5>Verification</h5>
                      <hr />
                    </Col>
                  </Row>
                  <Row>
                    <Col sm={6}>
                      <Row className="my-3">
                        <Col sm={3}>
                          <label>Verification Comment</label>
                        </Col>
                        <Col sm={9}>
                          <textarea
                            className="form-control"
                            name="statusDescription"
                            onBlur={handleBlur}
                            onChange={handleChange}
                            value={values.statusDescription}
                            rows="8"
                            placeholder="Enter your comment"
                          />
                          {touched.statusDescription && errors.statusDescription && (
                            <small className="text-danger form-text">{errors.statusDescription}</small>
                          )}
                        </Col>
                      </Row>
                    </Col>
                    <Col sm={6}>
                      <ChangeStatus
                        name="componentStatus"
                        values={values}
                        errors={errors}
                        touched={touched}
                        handleChange={handleChange}
                        handleBlur={handleBlur}
                        onStatusChange={saveComponentStatus}
                        options={evidenceUploadStatusOptions}
                      />
                      <Row className="my-3">
                        <Col sm={3}>
                          <label>Completed Date</label>
                        </Col>
                        <Col sm={9}>
                          <DateInput
                            name="completedDate"
                            onBlur={handleBlur}
                            onChange={handleChange}
                            value={values.completedDate}
                            disabled={isDisabled}
                          />
                          {touched.completedDate && errors.completedDate && (
                            <small className="text-danger form-text">{errors.completedDate}</small>
                          )}
                        </Col>
                      </Row>
                      <Row className="my-3">
                        <Col sm={3}>
                          <label>OFAC Verification Files</label>
                        </Col>
                        <Col sm={9}>
                          <MultiFileUpload
                            name="ofacEvidenceFile"
                            id="ofacEvidenceFile"
                            existingFiles={
                              ofacData?.ofacEvidence?.ofacEvidenceArray?.length > 0
                                ? ofacData.ofacEvidence.ofacEvidenceArray
                                : (ofacData?.ofacEvidence?.ofacEvidenceFile
                                  ? [ofacData.ofacEvidence.ofacEvidenceFile]
                                  : [])
                            }
                            existingURLs={
                              ofacData?.ofacEvidence?.ofacEvidenceURLArray?.length > 0
                                ? ofacData.ofacEvidence.ofacEvidenceURLArray
                                : (ofacData?.ofacEvidence?.ofacEvidenceURL
                                  ? [ofacData.ofacEvidence.ofacEvidenceURL]
                                  : [])
                            }
                            onFilesChange={handleFilesChange}
                            onDeleteFile={handleDeleteExistingFile}
                            disabled={isDisabled}
                          />
                          {(ofacData?.ofacEvidence?.ofacEvidenceArray?.length > 0 ||
                            ofacData?.ofacEvidence?.ofacEvidenceURLArray?.length > 0 ||
                            ofacData?.ofacEvidence?.ofacEvidenceURL ||
                            ofacData?.ofacEvidence?.ofacEvidenceFile) && (
                              <small style={{ cursor: 'pointer' }} className="text-primary form-text" onClick={handleViewGallery}>
                                View Gallery ({ofacData?.ofacEvidence?.ofacEvidenceArray?.length || (ofacData?.ofacEvidence?.ofacEvidenceFile ? 1 : 0)} files)
                              </small>
                            )}
                        </Col>
                      </Row>
                    </Col>
                  </Row>
                  {submitButtonActive && <Row className="my-3">
                    <Col sm={4}></Col>
                    <Col sm={4}>
                      <Button disabled={isEmptyObject(ofacData) || ofacData === "N.A."} className="btn-block" color="info" size="large" type="submit" variant="info">
                        Verify
                      </Button>
                    </Col>
                    <Col sm={4}></Col>
                  </Row>}
                </fieldset>
              </form>
            )}
          </Formik>
          <EvidenceGalleryModal
            show={showGallery}
            onHide={() => setShowGallery(false)}
            files={
              ofacData?.ofacEvidence?.ofacEvidenceArray?.length > 0
                ? ofacData.ofacEvidence.ofacEvidenceArray
                : (ofacData?.ofacEvidence?.ofacEvidenceFile
                  ? [ofacData.ofacEvidence.ofacEvidenceFile]
                  : [])
            }
            fileURLs={
              ofacData?.ofacEvidence?.ofacEvidenceURLArray?.length > 0
                ? ofacData.ofacEvidence.ofacEvidenceURLArray
                : (ofacData?.ofacEvidence?.ofacEvidenceURL
                  ? [ofacData.ofacEvidence.ofacEvidenceURL]
                  : [])
            }
            onDelete={handleGalleryDelete}
            canDelete={true}
          />
          {loader}
        </>
      )}
    </>
  );
};

export default OfacBgv;